/**
 * APIPage.jsx — Enumé Partner API Documentation
 * Place at: frontend/src/pages/APIPage.jsx
 *
 * Covers all 42 endpoints across 10 sections:
 *   Core, Challenges, Search, Graph, Creators,
 *   Workspaces (Cohorts), File Access & Upload,
 *   Webhooks, OAuth
 *
 * v3 Changes (June 2026):
 *   - Added Workspaces (Cohorts) section: 4 endpoints
 *   - Added File Access & Upload section: 4 endpoints
 *   - Updated hero, capability cards, and endpoint count
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Network, MapPin, GitBranch, Layers, Mail,
  ChevronDown, ChevronUp, Bell, Search, Users, Lock,
  Zap, Diamond, Globe, Shield,
} from 'lucide-react';


// ── Endpoint accordion item ──────────────────────────────────────────────────
function Endpoint({ method, path, description, params = [], response }) {
  const [open, setOpen] = useState(false);
  const methodColors = {
    GET:    'text-emerald-400  border-emerald-400/30  bg-emerald-400/5',
    POST:   'text-violet-400   border-violet-400/30   bg-violet-400/5',
    PUT:    'text-amber-400    border-amber-400/30    bg-amber-400/5',
    DELETE: 'text-red-400      border-red-400/30      bg-red-400/5',
  };

  return (
    <div className="border border-zinc-800 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-4 px-5 py-4 hover:bg-zinc-800/40 transition-colors text-left"
      >
        <span className={`font-mono text-xs font-bold px-2 py-1 rounded border ${methodColors[method] || methodColors.GET} shrink-0`}>
          {method}
        </span>
        <span className="font-mono text-sm text-zinc-200 flex-1 break-all">{path}</span>
        <span className="text-sm text-zinc-500 hidden md:block flex-1">{description}</span>
        {open
          ? <ChevronUp size={14} className="text-zinc-600 shrink-0" />
          : <ChevronDown size={14} className="text-zinc-600 shrink-0" />
        }
      </button>

      {open && (
        <div className="border-t border-zinc-800 px-5 py-5 space-y-5 bg-zinc-900/40">
          <p className="text-sm text-zinc-400">{description}</p>

          {params.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-zinc-500 uppercase tracking-widest mb-3">Parameters</p>
              <div className="space-y-2">
                {params.map((p, i) => (
                  <div key={i} className="flex gap-3 text-sm">
                    <span className="font-mono text-violet-400 w-40 shrink-0">{p.name}</span>
                    <span className="text-zinc-500 w-16 shrink-0">{p.type}</span>
                    <span className="text-zinc-400">{p.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {response && (
            <div>
              <p className="text-xs font-semibold text-zinc-500 uppercase tracking-widest mb-3">Example Response</p>
              <pre className="bg-zinc-950 border border-zinc-800 rounded-lg p-4 text-xs text-emerald-300 overflow-x-auto leading-relaxed">
                {response}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ── Section header ──────────────────────────────────────────────────────────
function SectionHeader({ icon: Icon, title, badge, description, color = 'violet' }) {
  const colors = {
    violet:  'text-violet-400  bg-violet-400/10 border-violet-400/20',
    emerald: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20',
    amber:   'text-amber-400   bg-amber-400/10   border-amber-400/20',
    cyan:    'text-cyan-400    bg-cyan-400/10    border-cyan-400/20',
    pink:    'text-pink-400    bg-pink-400/10    border-pink-400/20',
    red:     'text-red-400     bg-red-400/10     border-red-400/20',
    blue:    'text-blue-400    bg-blue-400/10    border-blue-400/20',
  };

  return (
    <div className="mb-6 mt-14 first:mt-0">
      <div className="flex items-center gap-3 mb-2">
        <div className={`p-2 rounded-lg border ${colors[color]}`}>
          <Icon size={16} />
        </div>
        <h3 className="text-xl font-bold text-white">{title}</h3>
        {badge && (
          <span className="text-xs font-mono px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-400 border border-zinc-700">
            {badge}
          </span>
        )}
      </div>
      {description && (
        <p className="text-sm text-zinc-400 ml-12">{description}</p>
      )}
    </div>
  );
}


// ── Use case card ────────────────────────────────────────────────────────────
function UseCaseCard({ icon: Icon, title, description }) {
  return (
    <div className="relative border border-zinc-800 rounded-xl p-6 bg-zinc-900/50 overflow-hidden group hover:border-zinc-600 transition-colors">
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
           style={{ background: 'radial-gradient(ellipse at top left, rgba(139,92,246,0.04) 0%, transparent 60%)' }} />
      <Icon size={22} className="text-violet-400 mb-4" />
      <h3 className="text-white font-semibold mb-2">{title}</h3>
      <p className="text-zinc-400 text-sm leading-relaxed">{description}</p>
    </div>
  );
}


// ── Main page ────────────────────────────────────────────────────────────────
export default function APIPage() {
  return (
    <div className="min-h-screen bg-zinc-950 text-white">

      {/* ── Nav ── */}
      <nav className="border-b border-zinc-800/60 px-6 py-4 flex items-center justify-between sticky top-0 bg-zinc-950/90 backdrop-blur-sm z-50">
        <Link to="/" className="flex items-center gap-2">
          <span className="text-lg font-bold tracking-tight">enume</span>
          <span className="text-zinc-600 text-sm">/</span>
          <span className="text-zinc-400 text-sm">API</span>
        </Link>
        <a
          href="mailto:support@enume.io?subject=API Partnership Inquiry"
          className="flex items-center gap-2 text-sm text-violet-400 hover:text-violet-300 transition-colors"
        >
          <Mail size={14} />
          Contact for access
        </a>
      </nav>

      {/* ── Hero ── */}
      <section className="max-w-4xl mx-auto px-6 pt-24 pb-20">
        <div className="inline-flex items-center gap-2 text-xs font-mono text-violet-400 border border-violet-400/20 bg-violet-400/5 rounded-full px-3 py-1 mb-8">
          <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-pulse" />
          Partner API v3 — 42 endpoints
        </div>

        <h1 className="text-5xl md:text-6xl font-bold tracking-tight leading-none mb-6">
          The connection graph your platform is missing.
        </h1>

        <p className="text-xl text-zinc-400 leading-relaxed max-w-2xl mb-10">
          Projects, challenges, creators, and the lineage graph that connects them all.
          Push notifications via webhooks. Private workspaces for firms.
          Programmatic file upload from any design tool. OAuth for acting on behalf of users.
        </p>

        <div className="flex flex-col sm:flex-row gap-4">
          <a
            href="mailto:support@enume.io?subject=API Partnership Inquiry"
            className="inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-500 text-white font-medium px-6 py-3 rounded-lg transition-colors"
          >
            Request access
            <ArrowRight size={16} />
          </a>
          <a
            href="#endpoints"
            className="inline-flex items-center gap-2 border border-zinc-700 hover:border-zinc-500 text-zinc-300 font-medium px-6 py-3 rounded-lg transition-colors"
          >
            View all endpoints
          </a>
        </div>
      </section>

      <div className="border-t border-zinc-800/60" />

      {/* ── Capabilities overview ── */}
      <section className="max-w-4xl mx-auto px-6 py-20">
        <p className="text-xs font-semibold text-zinc-500 uppercase tracking-widest mb-3">New in v3</p>
        <h2 className="text-3xl font-bold mb-12">Nine capabilities partners have been asking for.</h2>

        <div className="grid md:grid-cols-3 gap-5">
          <UseCaseCard
            icon={Layers}
            title="Private Workspaces"
            description="Cohort-scoped queries for firms and classrooms. See only your team's projects, filter by taxonomy labels, list members."
          />
          <UseCaseCard
            icon={Zap}
            title="File Upload"
            description="Push new versions and create projects from any tool — Revit, Blender, AutoCAD. Multipart upload with signed download URLs."
          />
          <UseCaseCard
            icon={Bell}
            title="Webhooks"
            description="Subscribe to events instead of polling. Get HMAC-signed push notifications within seconds of new projects, forks, and challenge responses."
          />
          <UseCaseCard
            icon={Diamond}
            title="Challenges"
            description="The collaboration engine. Browse open challenges, see responses, and track how students help each other across disciplines."
          />
          <UseCaseCard
            icon={Search}
            title="Advanced Search"
            description="Query the graph with 14 filter dimensions: zone, type, fork count, cross-zone connections, date range, tags, and more."
          />
          <UseCaseCard
            icon={Globe}
            title="Bulk Graph Export"
            description="Get the full constellation in one call — nodes, edges, cross-zone connections. No more walking the graph node by node."
          />
          <UseCaseCard
            icon={Users}
            title="Creator Profiles"
            description="See the people behind the projects. Public profiles, zone distribution, challenge stats, and portfolio listings."
          />
          <UseCaseCard
            icon={Lock}
            title="OAuth 2.0"
            description="Act on behalf of individual users with their consent. Full authorization code flow with scoped access tokens."
          />
        </div>
      </section>

      <div className="border-t border-zinc-800/60" />

      {/* ── Endpoints ── */}
      <section id="endpoints" className="max-w-4xl mx-auto px-6 py-20">
        <p className="text-xs font-semibold text-zinc-500 uppercase tracking-widest mb-3">Endpoints</p>
        <h2 className="text-3xl font-bold mb-4">42 endpoints. Zero ambiguity.</h2>
        <p className="text-zinc-400 mb-4">
          API key endpoints use the <span className="font-mono text-violet-400">X-API-Key</span> header.
          OAuth endpoints use <span className="font-mono text-violet-400">Bearer</span> tokens.
          Base URL: <span className="font-mono text-zinc-300 text-sm">https://enume.io</span>
        </p>
        <p className="text-zinc-500 text-sm mb-12">
          Rate limits: Free 500/day, Starter 10K/day, Enterprise unlimited. Shared across all endpoint groups.
        </p>

        <div className="space-y-3">

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/*  CORE                                                          */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <SectionHeader
            icon={Network}
            title="Core"
            badge="6 endpoints"
            description="Health check, project feed, connection graph, and asset tracking."
            color="violet"
          />

          <Endpoint method="GET" path="/api/partner/v1/health"
            description="Verify your API key and check daily usage."
            response={`{
  "status": "ok",
  "key_name": "Acme Corp",
  "tier": "starter",
  "used_today": 42,
  "remaining_today": 9958
}`} />

          <Endpoint method="GET" path="/api/partner/v1/community/projects"
            description="Public project feed, filterable by zone."
            params={[
              { name: 'zone',   type: 'string',  desc: 'build, taste, sound, story, smart, sell, heal, beautiful' },
              { name: 'limit',  type: 'integer', desc: 'Max results (default 50, max 100)' },
              { name: 'offset', type: 'integer', desc: 'Pagination offset' },
            ]}
            response={`{
  "total": 142,
  "projects": [{
    "id": 16,
    "name": "Handmade Guitar",
    "zone": "build",
    "project_type": "physical",
    "fork_count": 3,
    "license": "CC-BY-SA-4.0"
  }]
}`} />

          <Endpoint method="GET" path="/api/partner/v1/community/connections"
            description="Fork relationships between projects — the lineage graph."
            params={[
              { name: 'zone_from', type: 'string',  desc: 'Source zone filter' },
              { name: 'zone_to',   type: 'string',  desc: 'Target zone filter' },
              { name: 'limit',     type: 'integer', desc: 'Max results (default 50, max 100)' },
            ]} />

          <Endpoint method="POST" path="/api/partner/v1/assets/register"
            description="Register a project as a tracked asset in your integration."
            params={[
              { name: 'project_id',  type: 'integer', desc: 'Required' },
              { name: 'asset_name',  type: 'string',  desc: 'e.g. column_detail.dwg' },
              { name: 'application', type: 'string',  desc: 'blender, autocad, revit, etc.' },
            ]} />

          <Endpoint method="POST" path="/api/partner/v1/assets/relationship"
            description="Declare that one asset was derived from another."
            params={[
              { name: 'parent_project_id',  type: 'integer', desc: 'Source project (required)' },
              { name: 'child_project_id',   type: 'integer', desc: 'Derived project (required)' },
              { name: 'relationship_type',  type: 'string',  desc: 'derived-from | exported-to | referenced-in | inspired-by' },
              { name: 'parent_application', type: 'string',  desc: 'Source app' },
              { name: 'child_application',  type: 'string',  desc: 'Target app' },
            ]} />

          <Endpoint method="GET" path="/api/partner/v1/assets/:id/lineage"
            description="Full upstream/downstream lineage for any project."
            params={[
              { name: 'depth', type: 'integer', desc: 'Max depth (default 5, max 10)' },
            ]} />


          {/* ═══════════════════════════════════════════════════════════════ */}
          {/*  CHALLENGES                                                    */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <SectionHeader
            icon={Diamond}
            title="Challenges"
            badge="3 endpoints"
            description="Browse, filter, and inspect the collaboration engine."
            color="amber"
          />

          <Endpoint method="GET" path="/api/partner/v1/challenges"
            description="Browse open challenges with filtering and sort options."
            params={[
              { name: 'zone',   type: 'string',  desc: 'Filter by project zone' },
              { name: 'type',   type: 'string',  desc: 'general, ideas, feedback, etc.' },
              { name: 'q',      type: 'string',  desc: 'Text search across title + description' },
              { name: 'sort',   type: 'string',  desc: 'recent (default) | deadline | popular' },
              { name: 'limit',  type: 'integer', desc: 'Max results (default 50, max 100)' },
              { name: 'offset', type: 'integer', desc: 'Pagination offset' },
            ]}
            response={`{
  "total": 12,
  "challenges": [{
    "id": 5,
    "challenge_title": "Better font ideas?",
    "challenge_type": "ideas",
    "is_open": true,
    "response_count": 3,
    "project": {
      "id": 16,
      "name": "Poster Design",
      "zone": "beautiful"
    },
    "creator": { "id": 7, "username": "sarah_designs" }
  }]
}`} />

          <Endpoint method="GET" path="/api/partner/v1/challenges/:id"
            description="Challenge detail with responses and response projects."
            params={[
              { name: 'include_responses', type: 'boolean', desc: 'Include responses (default true, max 50)' },
            ]}
            response={`{
  "challenge": {
    "id": 5,
    "challenge_title": "Better font ideas?",
    "response_count": 3,
    "responses": [{
      "id": 8,
      "responder": { "id": 12, "username": "type_nerd" },
      "response_project": {
        "id": 44,
        "name": "Cabinet Grotesk Poster",
        "zone": "beautiful"
      }
    }]
  }
}`} />

          <Endpoint method="GET" path="/api/partner/v1/projects/:id/challenges"
            description="All challenges attached to a specific project." />


          {/* ═══════════════════════════════════════════════════════════════ */}
          {/*  SEARCH                                                        */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <SectionHeader
            icon={Search}
            title="Search"
            badge="1 endpoint"
            description="Advanced multi-dimensional project search."
            color="cyan"
          />

          <Endpoint method="GET" path="/api/partner/v1/search"
            description="Search projects with 14 filter dimensions. The most powerful query endpoint in the API."
            params={[
              { name: 'q',                    type: 'string',  desc: 'Full-text search (name, description, tags)' },
              { name: 'zone',                 type: 'string',  desc: 'Filter by zone' },
              { name: 'project_type',         type: 'string',  desc: 'physical | digital | hybrid' },
              { name: 'has_cross_zone_forks',  type: 'boolean', desc: 'Only projects forked into a different zone' },
              { name: 'min_forks',            type: 'integer', desc: 'Minimum fork count' },
              { name: 'min_views',            type: 'integer', desc: 'Minimum view count' },
              { name: 'created_after',        type: 'date',    desc: 'ISO date (e.g. 2026-01-01)' },
              { name: 'created_before',       type: 'date',    desc: 'ISO date' },
              { name: 'has_challenges',        type: 'boolean', desc: 'Only projects with open challenges' },
              { name: 'creator_id',           type: 'integer', desc: 'Filter by creator' },
              { name: 'tags',                 type: 'string',  desc: 'Comma-separated tag names' },
              { name: 'sort',                 type: 'string',  desc: 'recent | popular | most_forked | most_viewed' },
              { name: 'limit',                type: 'integer', desc: 'Max results (default 50, max 100)' },
              { name: 'offset',               type: 'integer', desc: 'Pagination offset' },
            ]}
            response={`{
  "total": 47,
  "projects": [{ "id": 16, "name": "Guitar", "zone": "build", ... }],
  "filters_applied": {
    "zone": "build",
    "has_cross_zone_forks": true,
    "sort": "most_forked"
  }
}`} />


          {/* ═══════════════════════════════════════════════════════════════ */}
          {/*  GRAPH                                                         */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <SectionHeader
            icon={Globe}
            title="Graph Export"
            badge="2 endpoints"
            description="Full constellation data in one call. No more walking node-by-node."
            color="emerald"
          />

          <Endpoint method="GET" path="/api/partner/v1/graph"
            description="Bulk export of all public projects as nodes and all fork relationships as edges."
            params={[
              { name: 'zone',                type: 'string',  desc: 'Filter to a single zone' },
              { name: 'include_challenges',  type: 'boolean', desc: 'Include challenge diamond nodes (default false)' },
              { name: 'format',              type: 'string',  desc: 'json (default) | adjacency' },
              { name: 'limit',               type: 'integer', desc: 'Max nodes (default 500, max 2000)' },
              { name: 'offset',              type: 'integer', desc: 'Pagination offset' },
            ]}
            response={`{
  "nodes": [
    { "id": 16, "name": "Guitar", "zone": "build", "node_type": "project", ... }
  ],
  "edges": [
    { "source": 16, "target": 20, "edge_type": "fork",
      "is_cross_zone": true, "source_zone": "build", "target_zone": "sound" }
  ],
  "total_nodes": 142,
  "cross_zone_edges": 47
}`} />

          <Endpoint method="GET" path="/api/partner/v1/graph/stats"
            description="High-level graph statistics. The proof that creative cross-pollination happens."
            response={`{
  "total_projects": 142,
  "total_connections": 89,
  "cross_zone_connections": 47,
  "cross_zone_percentage": 52.8,
  "open_challenges": 12,
  "total_creators": 38,
  "avg_forks_per_project": 1.6,
  "zone_breakdown": {
    "build": { "count": 28, "label": "Build Something", "color": "#f59e0b" },
    "sound": { "count": 22, "label": "Make It Sound Good", "color": "#06b6d4" }
  },
  "most_forked": [{ "id": 16, "name": "Guitar", "fork_count": 7 }]
}`} />


          {/* ═══════════════════════════════════════════════════════════════ */}
          {/*  CREATORS                                                      */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <SectionHeader
            icon={Users}
            title="Creators"
            badge="3 endpoints"
            description="Public profiles, stats, and project portfolios. Never exposes email or private data."
            color="pink"
          />

          <Endpoint method="GET" path="/api/partner/v1/creators"
            description="List creators with optional search and zone filtering."
            params={[
              { name: 'q',             type: 'string',  desc: 'Search by username or name' },
              { name: 'zone',          type: 'string',  desc: 'Creators with projects in this zone' },
              { name: 'min_projects',  type: 'integer', desc: 'Minimum project count' },
              { name: 'sort',          type: 'string',  desc: 'recent | most_projects | most_forked' },
              { name: 'limit',         type: 'integer', desc: 'Max results (default 50, max 100)' },
            ]}
            response={`{
  "total": 38,
  "creators": [{
    "id": 7,
    "username": "sarah_designs",
    "bio": "Industrial design student at Cabrillo",
    "stats": { "project_count": 12, "forks_received": 28 }
  }]
}`} />

          <Endpoint method="GET" path="/api/partner/v1/creators/:id"
            description="Detailed public profile with zone distribution and challenge stats."
            response={`{
  "creator": {
    "id": 7,
    "username": "sarah_designs",
    "stats": { "project_count": 12, "forks_received": 28, "total_views": 340 },
    "zone_distribution": { "build": 5, "beautiful": 4, "sound": 3 },
    "challenge_stats": {
      "challenges_created": 3,
      "challenge_responses_given": 8
    }
  }
}`} />

          <Endpoint method="GET" path="/api/partner/v1/creators/:id/projects"
            description="A creator's public project portfolio."
            params={[
              { name: 'zone', type: 'string',  desc: 'Filter by zone' },
              { name: 'sort', type: 'string',  desc: 'recent | popular | most_forked' },
              { name: 'limit', type: 'integer', desc: 'Max results (default 50, max 100)' },
            ]} />


          {/* ═══════════════════════════════════════════════════════════════ */}
          {/*  COHORTS / WORKSPACES                                          */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <SectionHeader
            icon={Layers}
            title="Workspaces (Cohorts)"
            badge="4 endpoints"
            description="Query projects within a team or classroom. Private cohorts require membership."
            color="cyan"
          />

          <Endpoint method="GET" path="/api/partner/v1/cohorts"
            description="List cohorts visible to this API key. Public cohorts always shown; private require membership."
            params={[
              { name: 'visibility', type: 'string',  desc: 'Filter: public | private | campus' },
              { name: 'limit',      type: 'integer', desc: 'Max results (default 50, max 100)' },
              { name: 'offset',     type: 'integer', desc: 'Pagination offset' },
            ]}
            response={`{
  "total": 8,
  "cohorts": [{
    "id": 5,
    "name": "Smith & Associates Studio",
    "slug": "smith-associates-studio",
    "visibility": "private",
    "memberCount": 12,
    "projectCount": 47,
    "isLocked": false,
    "maxMembers": 25
  }]
}`} />

          <Endpoint method="GET" path="/api/partner/v1/cohorts/:id"
            description="Cohort detail with taxonomy, member count, and instructor controls."
            response={`{
  "cohort": {
    "id": 5,
    "name": "Smith & Associates Studio",
    "visibility": "private",
    "memberCount": 12,
    "projectCount": 47,
    "isLocked": false,
    "expiresAt": null,
    "maxMembers": 25,
    "taxonomy": {
      "enabled": true,
      "labels": ["Residential", "Commercial", "Facade", "Interior"],
      "allow_student_additions": true
    }
  }
}`} />

          <Endpoint method="GET" path="/api/partner/v1/cohorts/:id/projects"
            description="All projects in a workspace. Filter by zone, taxonomy label, or sort order."
            params={[
              { name: 'zone',   type: 'string',  desc: 'Filter by zone (build, smart, etc.)' },
              { name: 'label',  type: 'string',  desc: 'Filter by taxonomy label (e.g. Residential)' },
              { name: 'sort',   type: 'string',  desc: 'recent | popular | most_forked' },
              { name: 'limit',  type: 'integer', desc: 'Max results (default 50, max 100)' },
              { name: 'offset', type: 'integer', desc: 'Pagination offset' },
            ]}
            response={`{
  "cohort_id": 5,
  "cohort_name": "Smith & Associates Studio",
  "total": 47,
  "taxonomy": { "labels": ["Residential", "Commercial", "Facade"] },
  "projects": [{
    "id": 16,
    "name": "Facade Study - Building A",
    "zone": "build",
    "cohort_labels": ["Residential", "Facade"],
    "fork_count": 3,
    "license": "CC-BY-SA-4.0"
  }]
}`} />

          <Endpoint method="GET" path="/api/partner/v1/cohorts/:id/members"
            description="Roster — username, role, join date. Never exposes email. Requires membership."
            response={`{
  "cohort_id": 5,
  "total": 12,
  "members": [{
    "user_id": 7,
    "username": "sarah_designs",
    "role": "admin",
    "joined_at": "2026-06-01T10:00:00"
  }]
}`} />


          {/* ═══════════════════════════════════════════════════════════════ */}
          {/*  FILE ACCESS & UPLOAD                                          */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <SectionHeader
            icon={Zap}
            title="File Access & Upload"
            badge="4 endpoints"
            description="Download project files, push new versions, and create projects from any design tool."
            color="amber"
          />

          <Endpoint method="GET" path="/api/partner/v1/projects/:id/files"
            description="List all files on a project with time-limited download URLs (2-hour expiry)."
            response={`{
  "project_id": 16,
  "project_name": "Facade Study",
  "total": 3,
  "files": [
    {
      "type": "cover_image",
      "filename": "facade_render.jpg",
      "download_url": "https://storage.blob.../facade_render.jpg?sv=...&sig=..."
    },
    {
      "type": "digital_file",
      "id": 42,
      "filename": "facade_v3.rvt",
      "file_type": "rvt",
      "file_size": 2458624,
      "download_url": "https://storage.blob.../facade_v3.rvt?sv=...&sig=..."
    }
  ]
}`} />

          <Endpoint method="GET" path="/api/partner/v1/projects/:id/versions/:vid/download"
            description="Get a signed download URL for a specific version's file."
            response={`{
  "project_id": 16,
  "version_id": 3,
  "version_number": 3,
  "filename": "facade_v3.rvt",
  "file_size": 2458624,
  "download_url": "https://storage.blob.../facade_v3.rvt?sv=...&sig=...",
  "expires_in": "2 hours"
}`} />

          <Endpoint method="POST" path="/api/partner/v1/projects/:id/versions"
            description="Upload a new version of a project file. Multipart form data. API key creator must own the project."
            params={[
              { name: 'file',  type: 'file',   desc: 'The file to upload (required, multipart)' },
              { name: 'notes', type: 'string',  desc: 'Version notes, e.g. "Updated mullion detail"' },
            ]}
            response={`{
  "message": "Version uploaded successfully",
  "project_id": 16,
  "version": {
    "id": 17,
    "version_number": 4,
    "filename": "facade_v4.rvt",
    "file_size": 2512384,
    "notes": "Updated mullion detail per client feedback",
    "download_url": "https://storage.blob.../facade_v4.rvt?sv=...&sig=..."
  }
}`} />

          <Endpoint method="POST" path="/api/partner/v1/projects"
            description="Create a new project. Supports file upload, cover image, tags, zone, and cohort linking in one call."
            params={[
              { name: 'name',              type: 'string',  desc: 'Project name (required)' },
              { name: 'description',       type: 'string',  desc: 'Project description' },
              { name: 'project_type',      type: 'string',  desc: 'physical | digital | hybrid (default: digital)' },
              { name: 'category',          type: 'string',  desc: 'Zone: build, beautiful, sound, taste, story, heal, smart, sell' },
              { name: 'tags',              type: 'string',  desc: 'Comma-separated tags, e.g. "revit,facade,residential"' },
              { name: 'file',              type: 'file',    desc: 'Initial project file (multipart)' },
              { name: 'cover_image',       type: 'file',    desc: 'Cover image for the map node (multipart)' },
              { name: 'parent_project_id', type: 'integer', desc: 'Fork from this project' },
              { name: 'cohort_id',         type: 'integer', desc: 'Link to this cohort (private cohort = private project)' },
            ]}
            response={`{
  "message": "Project created successfully",
  "project": {
    "id": 55,
    "name": "Facade Study - Building A",
    "zone": "build",
    "project_type": "digital",
    "version": {
      "id": 1,
      "version_number": 1,
      "filename": "facade_v1.rvt",
      "download_url": "https://storage.blob.../facade_v1.rvt?sv=...&sig=..."
    }
  }
}`} />


          {/* ═══════════════════════════════════════════════════════════════ */}
          {/*  WEBHOOKS                                                      */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <SectionHeader
            icon={Bell}
            title="Webhooks"
            badge="6 endpoints"
            description="Push notifications instead of polling. HMAC-SHA256 signed payloads."
            color="red"
          />

          <Endpoint method="POST" path="/api/partner/v1/webhooks"
            description="Register a webhook URL. Returns the signing_secret (shown only once)."
            params={[
              { name: 'url',         type: 'string', desc: 'HTTPS URL to receive events (required)' },
              { name: 'events',      type: 'array',  desc: 'Event types to subscribe to (required)' },
              { name: 'description', type: 'string', desc: 'Human-readable label' },
            ]}
            response={`{
  "webhook": {
    "id": 1,
    "url": "https://acme.com/webhooks/enume",
    "events": ["project.created", "project.forked", "challenge.created"],
    "signing_secret": "a1b2c3..."
  },
  "verification_example": {
    "algorithm": "HMAC-SHA256",
    "message": "<timestamp>.<payload_json>"
  }
}`} />

          <Endpoint method="GET" path="/api/partner/v1/webhooks"
            description="List all your registered webhooks." />

          <Endpoint method="PUT" path="/api/partner/v1/webhooks/:id"
            description="Update a webhook's URL, events, or active status."
            params={[
              { name: 'url',       type: 'string',  desc: 'New HTTPS URL' },
              { name: 'events',    type: 'array',   desc: 'Updated event subscriptions' },
              { name: 'is_active', type: 'boolean', desc: 'Pause/resume delivery' },
            ]} />

          <Endpoint method="DELETE" path="/api/partner/v1/webhooks/:id"
            description="Remove a webhook and its delivery logs." />

          <Endpoint method="POST" path="/api/partner/v1/webhooks/:id/test"
            description="Send a test.ping event to verify your endpoint is reachable."
            response={`{
  "delivered": true,
  "response_status": 200,
  "response_body": "OK"
}`} />

          <Endpoint method="GET" path="/api/partner/v1/webhooks/:id/logs"
            description="Delivery log with success rate. Debug failed deliveries."
            params={[
              { name: 'limit', type: 'integer', desc: 'Max results (default 50, max 100)' },
              { name: 'event', type: 'string',  desc: 'Filter by event type' },
            ]}
            response={`{
  "total_delivered": 128,
  "total_failed": 2,
  "success_rate": 98.5,
  "logs": [{
    "event_type": "project.created",
    "response_status": 200,
    "delivered": true,
    "created_at": "2026-05-29T18:30:00Z"
  }]
}`} />


          {/* ═══════════════════════════════════════════════════════════════ */}
          {/*  WEBHOOK EVENTS REFERENCE                                      */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <div className="border border-zinc-800 rounded-lg p-5 bg-zinc-900/30 mt-4">
            <p className="text-xs font-semibold text-zinc-500 uppercase tracking-widest mb-4">Webhook Events</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              {[
                ['project.created',     'New public project published'],
                ['project.forked',      'A project was forked'],
                ['project.updated',     'Project metadata changed'],
                ['project.deleted',     'Project removed'],
                ['challenge.created',   'New challenge posted'],
                ['challenge.responded', 'Someone responded to a challenge'],
                ['challenge.closed',    'Challenge marked as filled'],
              ].map(([event, desc], i) => (
                <div key={i} className="flex gap-3 items-start">
                  <span className="font-mono text-emerald-400 shrink-0">{event}</span>
                  <span className="text-zinc-400">{desc}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 p-4 bg-zinc-950 border border-zinc-800 rounded-lg">
              <p className="text-xs text-zinc-500 mb-2">Delivery headers on every event:</p>
              <pre className="text-xs text-zinc-300 leading-relaxed">{`X-Enume-Event:     project.created
X-Enume-Timestamp: 1717100000
X-Enume-Signature: hmac_sha256(secret, "1717100000.{payload}")`}</pre>
            </div>
          </div>


          {/* ═══════════════════════════════════════════════════════════════ */}
          {/*  OAUTH                                                         */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <SectionHeader
            icon={Lock}
            title="OAuth 2.0"
            badge="5 endpoints"
            description="Authorization code flow. Act on behalf of individual users with their consent."
            color="blue"
          />

          <Endpoint method="GET" path="/api/partner/oauth/authorize"
            description="Step 1: Redirect the user here. On approval, redirects to your redirect_uri with a code."
            params={[
              { name: 'client_id',     type: 'string', desc: 'Your OAuth client ID (required)' },
              { name: 'redirect_uri',  type: 'string', desc: 'Must match a registered URI (required)' },
              { name: 'scope',         type: 'string', desc: 'Space-separated scopes' },
              { name: 'state',         type: 'string', desc: 'CSRF protection (returned unchanged)' },
              { name: 'response_type', type: 'string', desc: 'Must be "code"' },
            ]} />

          <Endpoint method="POST" path="/api/partner/oauth/token"
            description="Step 2: Exchange code for tokens, or refresh an expired access_token."
            params={[
              { name: 'grant_type',    type: 'string', desc: 'authorization_code | refresh_token' },
              { name: 'client_id',     type: 'string', desc: 'Your client ID' },
              { name: 'client_secret', type: 'string', desc: 'Your client secret' },
              { name: 'code',          type: 'string', desc: 'Authorization code (for auth code grant)' },
              { name: 'redirect_uri',  type: 'string', desc: 'Must match the one used in /authorize' },
              { name: 'refresh_token', type: 'string', desc: 'Refresh token (for refresh grant)' },
            ]}
            response={`{
  "access_token": "eat_...",
  "refresh_token": "ert_...",
  "token_type": "Bearer",
  "expires_in": 3600,
  "scope": "read:projects read:profile"
}`} />

          <Endpoint method="POST" path="/api/partner/oauth/revoke"
            description="Revoke an access token or refresh token."
            params={[
              { name: 'token',         type: 'string', desc: 'The token to revoke' },
              { name: 'client_id',     type: 'string', desc: 'Your client ID' },
              { name: 'client_secret', type: 'string', desc: 'Your client secret' },
            ]} />

          <Endpoint method="GET" path="/api/partner/oauth/userinfo"
            description="Get the authenticated user's public profile. Requires Bearer token with read:profile scope."
            response={`{
  "id": 7,
  "username": "sarah_designs",
  "first_name": "Sarah",
  "bio": "Industrial design student",
  "skills": ["ceramics", "3d-printing", "woodworking"]
}`} />

          <Endpoint method="GET" path="/api/partner/oauth/projects"
            description="Get the authenticated user's public projects. Requires read:projects scope."
            params={[
              { name: 'limit',  type: 'integer', desc: 'Max results (default 50)' },
              { name: 'offset', type: 'integer', desc: 'Pagination offset' },
            ]} />


          {/* ═══════════════════════════════════════════════════════════════ */}
          {/*  SCOPES REFERENCE                                              */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <div className="border border-zinc-800 rounded-lg p-5 bg-zinc-900/30 mt-4">
            <p className="text-xs font-semibold text-zinc-500 uppercase tracking-widest mb-4">OAuth Scopes</p>
            <div className="space-y-2 text-sm">
              {[
                ['read:projects',    'Read the user\'s public projects'],
                ['write:projects',   'Create or edit projects on behalf of the user'],
                ['read:challenges',  'Read challenges and responses'],
                ['write:challenges', 'Create challenges and respond to challenges'],
                ['read:profile',     'Read the user\'s profile info'],
              ].map(([scope, desc], i) => (
                <div key={i} className="flex gap-3 items-start">
                  <span className="font-mono text-blue-400 w-44 shrink-0">{scope}</span>
                  <span className="text-zinc-400">{desc}</span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      <div className="border-t border-zinc-800/60" />

      {/* ── CTA ── */}
      <section className="max-w-4xl mx-auto px-6 py-24 text-center">
        <h2 className="text-4xl font-bold mb-4">Ready to integrate?</h2>
        <p className="text-zinc-400 text-lg mb-10 max-w-xl mx-auto">
          The Enumé Partner API is available by invitation.
          Reach out to discuss your use case and we'll get you set up.
        </p>
        <a
          href="mailto:support@enume.io?subject=API Partnership Inquiry"
          className="inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-500 text-white font-semibold px-8 py-4 rounded-lg transition-colors text-lg"
        >
          <Mail size={18} />
          support@enume.io
        </a>
        <p className="text-zinc-600 text-sm mt-6">
          We respond to all partnership inquiries within 48 hours.
        </p>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-zinc-800/60 px-6 py-8">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-zinc-600 text-sm">&copy; 2026 Enumé. All rights reserved.</span>
          <div className="flex gap-6 text-sm text-zinc-600">
            <Link to="/terms"   className="hover:text-zinc-400 transition-colors">Terms</Link>
            <Link to="/privacy" className="hover:text-zinc-400 transition-colors">Privacy</Link>
            <Link to="/dmca"    className="hover:text-zinc-400 transition-colors">DMCA</Link>
          </div>
        </div>
      </footer>

    </div>
  );
}